// OnboardingChecklist.tsx — First-login walkthrough for new carriers
// Shows in Command Center until all steps are complete
// Progress stored in localStorage per company so it doesn't reappear

import React, { useState, useEffect } from 'react';

interface Step {
  id:       string;
  title:    string;
  detail:   string;
  action?:  string;
  link?:    string;
}

const STEPS: Step[] = [
  { id:'driver',   title:'Add your first driver',     detail:'Go to Driver Management → + CREATE ENTRY',          action:'Driver Management' },
  { id:'eld',      title:'Connect your ELD',          detail:'Go to Settings → ELD Provider → enter your API key', action:'Settings' },
  { id:'load',     title:'Create your first load',    detail:'Go to Control Tower → + CREATE ENTRY',               action:'Control Tower' },
  { id:'jake',     title:'Talk to Jake',              detail:'Click ACTIVATE JAKE and say "Jake, what loads are active?"' },
  { id:'broker',   title:'Give brokers your email',   detail:'Settings → Email Automation → share your inbound address', action:'Settings' },
  { id:'driver_app', title:'Set up the driver app',  detail:'Have a driver go to app.blacktopsystem.com/driver and tap Add to Home Screen' },
  { id:'factoring', title:'Connect your factor',      detail:'Settings → Factoring → add your provider and API key (optional)', action:'Settings' },
];

interface Props {
  companyId: string | null;
  setRoom:   (room: string) => void;
}

export default function OnboardingChecklist({ companyId, setRoom }: Props) {
  const key = `onboarding_${companyId}`;
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.dismissed) { setDismissed(true); return; }
      setCompleted(parsed.completed || {});
    }
  }, [key]);

  const save = (newCompleted: Record<string, boolean>) => {
    localStorage.setItem(key, JSON.stringify({ completed: newCompleted }));
    setCompleted(newCompleted);
  };

  const check = (id: string) => {
    const next = { ...completed, [id]: true };
    save(next);
  };

  const dismiss = () => {
    localStorage.setItem(key, JSON.stringify({ dismissed: true, completed }));
    setDismissed(true);
  };

  if (dismissed) return null;

  const doneCount = Object.values(completed).filter(Boolean).length;
  const total     = STEPS.length;
  const pct       = Math.round((doneCount / total) * 100);
  const allDone   = doneCount === total;

  if (allDone) return (
    <div style={{ background:'#050505', border:'1px solid #32CD32', padding:'16px 20px', marginBottom:'16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <div style={{ color:'#32CD32', fontWeight:700, fontSize:'13px' }}>✓ Setup complete — you're fully operational</div>
      <button onClick={dismiss} style={{ background:'transparent', border:'none', color:'#555', cursor:'pointer', fontFamily:'monospace', fontSize:'11px' }}>DISMISS</button>
    </div>
  );

  return (
    <div style={{ background:'#050505', border:'1px solid #1e1e1e', marginBottom:'16px' }}>
      <div style={{ padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', borderBottom: collapsed ? 'none' : '1px solid #1a1a1a' }} onClick={() => setCollapsed(!collapsed)}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ color:'#32CD32', fontSize:'10px', fontWeight:700, letterSpacing:'2px' }}>GETTING STARTED</div>
          <div style={{ background:'#1a1a1a', borderRadius:'99px', height:'6px', width:'120px', overflow:'hidden' }}>
            <div style={{ background:'#32CD32', height:'100%', width:`${pct}%`, transition:'width 0.3s' }} />
          </div>
          <div style={{ color:'#555', fontSize:'10px' }}>{doneCount}/{total} complete</div>
        </div>
        <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
          <button onClick={e => { e.stopPropagation(); dismiss(); }} style={{ background:'transparent', border:'none', color:'#333', cursor:'pointer', fontFamily:'monospace', fontSize:'10px' }}>DISMISS</button>
          <span style={{ color:'#555', fontSize:'12px' }}>{collapsed ? '▼' : '▲'}</span>
        </div>
      </div>

      {!collapsed && (
        <div style={{ padding:'12px 20px' }}>
          {STEPS.map(step => {
            const done = !!completed[step.id];
            return (
              <div key={step.id} style={{ display:'flex', alignItems:'flex-start', gap:'12px', padding:'10px 0', borderBottom:'1px solid #0a0a0a' }}>
                <div onClick={() => check(step.id)} style={{ width:'18px', height:'18px', border:`1px solid ${done ? '#32CD32' : '#333'}`, background: done ? '#32CD32' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, marginTop:'1px' }}>
                  {done && <span style={{ color:'#000', fontSize:'11px', fontWeight:700 }}>✓</span>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ color: done ? '#555' : '#fff', fontSize:'12px', fontWeight:700, textDecoration: done ? 'line-through' : 'none', marginBottom:'2px' }}>{step.title}</div>
                  <div style={{ color:'#555', fontSize:'11px' }}>{step.detail}</div>
                </div>
                {step.action && !done && (
                  <button onClick={() => { setRoom(step.action!); check(step.id); }} style={{ background:'transparent', border:'1px solid #333', color:'#aaa', padding:'4px 10px', fontFamily:'monospace', fontSize:'10px', cursor:'pointer', whiteSpace:'nowrap' as const }}>
                    GO →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
